import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { isAxiosError } from 'axios'
import { useAuthStore } from '@/stores/Auth'
import NotFound from '../views/NotFound.vue'
import authGuard from './guards/authGuard'

import Login from '../views/Auth/Login.vue'
import AuthApi from '@/helpers/api/AuthApi'

// Sign-in and the 404 page load with the app; every other screen loads when first opened, so the
// first page does not wait for the invoice, customer, user and settings screens.
const VerifyEmail = () => import('../views/Auth/VerifyEmail.vue')
const ForgotPassword = () => import('../views/Auth/ForgotPassword.vue')
const ResetPassword = () => import('../views/Auth/ResetPassword.vue')
const UserList = () => import('../views/User/List.vue')
const UserEdit = () => import('../views/User/Edit.vue')
const UserDetails = () => import('../views/User/Details.vue')
const CustomerList = () => import('../views/Customer/List.vue')
const CustomerEdit = () => import('../views/Customer/Edit.vue')
const CustomerDetails = () => import('../views/Customer/Details.vue')
const BusinessProfileSettings = () => import('../views/BusinessProfile/Settings.vue')
const InvoiceList = () => import('../views/Invoice/List.vue')
const InvoiceEdit = () => import('../views/Invoice/Edit.vue')
const InvoiceDetails = () => import('../views/Invoice/Details.vue')

// Invoices are the landing screen: the root, sign-in and every redirect of a route the user may not
// open lead there. Routes with meta.roles open only for those roles (authGuard); the navigation
// shows the same links.
export const routes: RouteRecordRaw[] = [
	{
		path: '/verify-email',
		name: 'verify-email-notice',
		component: VerifyEmail,
		meta: {
			noNavigation: true,
		},
	},
	{
		path: '/email/verify/:id/:hash',
		name: 'email-verification',
		component: () => {},
		beforeEnter: async (to) => {
			const auth = useAuthStore()
			await auth.getUser()

			if (!auth.user) {
				return { name: 'login' }
			}

			try {
				await new AuthApi().verifyEmail({
					id: to.params.id as string,
					hash: to.params.hash as string,
					expires: to.query.expires as string,
					signature: to.query.signature as string,
				})
			} catch (error) {
				// The API refuses an expired or altered link with 400, and one for another account
				// or address (user or hash mismatch) with 403. Anything else, such as no connection,
				// is a failure to verify. The notice says which, and can send a new link.
				const status = isAxiosError(error) ? error.response?.status : undefined
				const refused = status === 400 || status === 403
				return { name: 'verify-email-notice', query: { link: refused ? 'invalid' : 'failed' } }
			}

			await auth.getUser()

			return { name: 'root' }
		},
		meta: {
			unprotected: true,
			noNavigation: true,
		},
	},
	{
		path: '/forgot-password',
		name: 'forgot-password',
		component: ForgotPassword,
		meta: {
			unprotected: true,
			noNavigation: true,
		},
	},
	{
		path: '/reset-password',
		name: 'reset-password',
		component: ResetPassword,
		meta: {
			unprotected: true,
			noNavigation: true,
		},
	},
	{
		path: '/login',
		name: 'login',
		component: Login,
		meta: {
			avoidUser: true,
			noNavigation: true,
		},
	},
	{
		path: '/logout',
		name: 'logout',
		component: () => {},
		beforeEnter: async () => {
			const auth = useAuthStore()
			await auth.logout()
			return { name: 'login' }
		},
		meta: {
			unprotected: true,
			noNavigation: true,
		},
	},
	{
		path: '/users',
		children: [
			{
				path: '',
				name: 'users-list',
				component: UserList,
				meta: {
					roles: ['admin'],
				},
			},
			{
				path: '/users/create',
				name: 'users-create',
				component: UserEdit,
				meta: {
					roles: ['admin'],
				},
			},
			{
				path: '/users/:id',
				name: 'users-edit',
				component: UserEdit,
				meta: {
					roles: ['admin'],
				},
			},
		],
	},
	{
		path: '/customers',
		children: [
			{
				path: '',
				name: 'customers-list',
				component: CustomerList,
			},
			{
				path: '/customers/create',
				name: 'customers-create',
				component: CustomerEdit,
			},
			{
				path: '/customers/:id',
				name: 'customers-details',
				component: CustomerDetails,
			},
			{
				path: '/customers/:id/edit',
				name: 'customers-edit',
				component: CustomerEdit,
			},
		],
	},
	// Business settings and initial numbering are changed only by admins (PLAN section 2).
	{
		path: '/business-profile',
		name: 'business-profile',
		component: BusinessProfileSettings,
		meta: {
			roles: ['admin'],
		},
	},
	{
		path: '/invoices',
		children: [
			{
				path: '',
				name: 'invoices-list',
				component: InvoiceList,
			},
			{
				path: '/invoices/create',
				name: 'invoices-create',
				component: InvoiceEdit,
			},
			// An issued or void invoice. A draft opened here, also through an older link, moves
			// to its editor.
			{
				path: '/invoices/:id',
				name: 'invoices-details',
				component: InvoiceDetails,
			},
			// A draft. An issued or void invoice opened here moves to its page, unless the
			// editor still shows work that could not be saved.
			{
				path: '/invoices/:id/edit',
				name: 'invoices-edit',
				component: InvoiceEdit,
			},
		],
	},
	{
		path: '/404-not-found',
		name: 'not-found',
		component: NotFound,
		meta: {
			unprotected: true,
		},
	},
	{
		path: '/account',
		children: [
			{
				path: '',
				name: 'account-details',
				component: UserDetails,
			},
		],
	},
	// The root and any path without a route, such as the removed generator screens.
	{
		name: 'root',
		path: '/:pathMatch(.*)*',
		redirect: { name: 'invoices-list' },
	},
]

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
})

router.beforeEach(authGuard)

export default router
